import React from "react";
import { Link, useParams } from "react-router-dom";
import SeoHelmet, { breadcrumbSchema } from "../components/SeoHelmet";
import { AuthorByline, AuthorBioCard } from "../components/Author";
import Breadcrumbs from "../components/Breadcrumbs";
import { BLOG_POSTS } from "../data/blog";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function BlogPost({ slug, children }) {
    const params = useParams();
    const realSlug = slug || params.slug;
    const post = BLOG_POSTS.find((p) => p.slug === realSlug) || BLOG_POSTS[0];
    const others = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);
    const { user } = useAuth();
    const isPaid = !!user && (user.is_paid || user.role === "admin");
    return (
        <>
            <SeoHelmet
                title={`${post.title}`}
                description={post.excerpt}
                schema={breadcrumbSchema([
                    { label: "Home", href: "/" },
                    { label: "Blog", href: "/blog" },
                    { label: post.title, href: `/blog/${post.slug}` },
                ])}
            />
            <article className="mx-auto max-w-3xl px-4 md:px-8 py-10">
                <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Blog", href: "/blog" }, { label: post.category }]} />
                <div className="mt-3 ui-heading text-xs uppercase tracking-widest text-cobalt">/ {post.category}</div>
                <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl mt-3 leading-[0.95]">{post.title}</h1>
                <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
                    <AuthorByline date={post.date} readTime={post.readTime} />
                </div>
                <hr className="my-8 border-black/10" />
                <div className="prose-blog space-y-5 text-ink leading-relaxed">{children}</div>

                <hr className="my-10 border-black/10" />
                <AuthorBioCard />

                {!isPaid && (
                    <div className="mt-10 border border-cobalt/30 bg-cobalt/5 p-6 text-center" data-testid="blog-cta-card">
                        <h3 className="font-display font-black tracking-tighter text-2xl">Try OpenCSV Pro free for 14 days</h3>
                        <p className="mt-2 text-sm text-ink-soft">Open, edit, save, and share CSV files on any device. No subscription. Ever.</p>
                        <Link to={user ? "/pricing" : "/signup"} className="btn-brutal mt-4 inline-flex items-center gap-2 bg-cobalt text-white px-5 py-2.5 ui-heading text-sm font-semibold border border-cobalt" data-testid="blog-cta-buy">Start free trial <ArrowRight size={14} /></Link>
                    </div>
                )}

                {others.length > 0 && (
                    <section className="mt-12">
                        <h3 className="ui-heading text-sm uppercase tracking-widest text-ink-muted mb-3">More articles</h3>
                        <ul className="space-y-3">
                            {others.map((p) => (
                                <li key={p.slug}>
                                    <Link to={`/blog/${p.slug}`} className="block border border-black/10 bg-white hover:border-cobalt p-4">
                                        <div className="ui-heading text-xs uppercase tracking-widest text-cobalt">/ {p.category}</div>
                                        <div className="font-display font-black tracking-tighter text-lg mt-1">{p.title}</div>
                                        <p className="text-sm text-ink-soft mt-1">{p.excerpt}</p>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </article>
        </>
    );
}
